import classNames from 'classnames'
import _ from 'lodash'
import React, {useContext, useLayoutEffect, useMemo, useRef, useState} from 'react'
import {Link} from 'react-router-dom'
import {Mention} from './Mention'
import FeedContext from "./FeedContext";
import {checkAllowUrl} from "../../helpers/stringUtils";
import Ahref from "../Ahref";
import copyToClipboard from "copy-to-clipboard";
import {message} from "antd";
import useTranslate from "../../modules/local/useTranslate";
import {HiArrowUpRight} from "react-icons/hi2";
import {SlLink} from "react-icons/sl";
import {BsCameraVideo, BsMusicNoteBeamed} from "react-icons/bs";

const AUDIO_FILE_EXTENSIONS = ['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac', 'wma']
const VIDEO_FILE_EXTENSIONS = ['mp4', 'mov', 'avi', 'webm', 'mkv', 'flv', 'wmv', 'm4v']

// Nếu URL trỏ thẳng tới file audio/video (không phải trang web) → trả về loại file
// để đổi icon link cho đúng ngữ nghĩa (SlLink chỉ hợp cho link trang thường)
function getLinkFileType(url) {
    let pathname
    try {
        pathname = new URL(url).pathname.toLowerCase()
    } catch (_) {
        return null
    }
    const ext = pathname.split('.').pop()
    if (AUDIO_FILE_EXTENSIONS.includes(ext)) return 'audio'
    if (VIDEO_FILE_EXTENSIONS.includes(ext)) return 'video'
    return null
}

function testUrl(string) {
    let url
    try {
        url = new URL(string)
    } catch (_) {
        return false
    }
    return url.protocol === 'http:' || url.protocol === 'https:'
}

// Truncate URL display: chỉ hiển thị hostname, ẩn path — path (nếu có) được
// biểu thị bằng icon mũi tên + "..." thay vì nhúng ký tự cắt vào text
// Ví dụ: example.com/ ↗ ...
const formatUrlDisplay = (token) => {
    let host, hasPath
    try {
        const u = new URL(token)
        host = u.hostname
        hasPath = (u.pathname !== '/' && u.pathname !== '') || !!u.search || !!u.hash
    } catch {
        host = token
        hasPath = false
    }
    return {text: hasPath ? `${host}/` : host, hasPath}
}

// Combined regex: Markdown inline tokens + custom entity tokens
// Order matters: more-specific patterns come before less-specific ones
const INLINE_PATTERN = [
    '\\*\\*[^*\\n]+\\*\\*',      // **bold**
    '__[^_\\n]+__',               // __bold__
    '~~[^~\\n]+~~',               // ~~strikethrough~~
    '`[^`\\n]+`',                 // `code`
    '\\*[^*\\n]+\\*',             // *italic*
    '_[^_\\n]+_',                 // _italic_
    '\\[[^\\]\\n]+\\]\\([^)\\n]+\\)', // [text](url)
    '@[^\\s]+',                   // @mention
    '#url\\$[^$\\s]+\\$[^\\s]+',  // #url$url$text
    '#\\w+',                      // #tag
    'https?:\\/\\/[^\\s]+',       // plain URL
].join('|')

// Parse a string into an array of strings and React elements.
// Creates a new RegExp each call to avoid shared lastIndex state across recursive calls.
const parseInline = (str, mentions) => {
    if (!str) return []
    const result = []
    const regex = new RegExp(INLINE_PATTERN, 'g')
    let lastIndex = 0
    let keyIdx = 0
    let match

    while ((match = regex.exec(str)) !== null) {
        const start = match.index
        const end = regex.lastIndex
        const token = match[0]

        if (start > lastIndex) {
            result.push(str.slice(lastIndex, start))
        }

        const key = keyIdx++

        if (token.startsWith('**') || token.startsWith('__')) {
            const inner = token.slice(2, -2)
            result.push(<strong key={key}>{parseInline(inner, mentions)}</strong>)

        } else if (token.startsWith('~~')) {
            const inner = token.slice(2, -2)
            result.push(<del key={key}>{parseInline(inner, mentions)}</del>)

        } else if (token.startsWith('`')) {
            result.push(
                <code key={key} className="bg-gray-100 rounded px-1 text-sm font-mono">
                    {token.slice(1, -1)}
                </code>
            )

        } else if (token.startsWith('*') || token.startsWith('_')) {
            const inner = token.slice(1, -1)
            result.push(<em key={key}>{parseInline(inner, mentions)}</em>)

        } else if (token.startsWith('[')) {
            const m = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/)
            if (m) {
                const [, linkText, href] = m
                const isValid = checkAllowUrl(href)
                result.push(
                    <Ahref
                        key={key}
                        className="italic underline text-color-100 text-sm leading-snug"
                        href={isValid ? href : undefined}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => {
                            copyToClipboard(href);
                            message.info('copied')
                        }}
                    >
                        {linkText}
                    </Ahref>
                )
            } else {
                result.push(token)
            }

        } else if (token.startsWith('@')) {
            result.push(
                <Mention key={key} prefix="@" mentions={mentions}>{token.slice(1)}</Mention>
            )

        } else if (token.startsWith('#url$')) {
            const parts = token.split('$')
            const url = parts[1]
            const text = parts[2]
            const isValid = checkAllowUrl(url)
            result.push(
                <Ahref
                    key={key}
                    className="italic underline text-color-100 text-sm leading-snug"
                    href={isValid ? url : undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                        copyToClipboard(url);
                        message.info('copied')
                    }}>
                    {decodeURI(text)}
                </Ahref>
            )

        } else if (token.startsWith('#')) {
            result.push(
                <Link
                    key={key}
                    className="text-link text-sm"
                    to={`/t/${token.substring(1)}`}>
                    {token}
                </Link>
            )

        } else if (testUrl(token)) {
            const isValid = checkAllowUrl(token)
            const {text, hasPath} = formatUrlDisplay(token)
            const fileType = getLinkFileType(token)
            const LinkIcon = fileType === 'audio' ? BsMusicNoteBeamed : fileType === 'video' ? BsCameraVideo : SlLink
            result.push(
                <Ahref
                    key={key}
                    className="inline-flex items-center gap-0.5 italic underline text-link text-sm leading-snug"
                    href={isValid ? token : undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                        copyToClipboard(token);
                        message.info('copied')
                    }}>
                    <LinkIcon size={12} className="flex-shrink-0"/>
                    <span>{text}</span>
                    {hasPath && (
                        <>
                            <HiArrowUpRight size={12} className="flex-shrink-0"/>
                            <span>...</span>
                        </>
                    )}
                </Ahref>
            )

        } else {
            result.push(token)
        }

        lastIndex = end
    }

    if (lastIndex < str.length) {
        result.push(str.slice(lastIndex))
    }

    return result
}

// Parse content into block-level elements
const parseBlocks = (content) => {
    const lines = content.split('\n')
    const blocks = []
    let i = 0

    while (i < lines.length) {
        const line = lines[i]

        // Fenced code block
        if (/^```/.test(line)) {
            const lang = line.slice(3).trim()
            const codeLines = []
            i++
            while (i < lines.length && !/^```/.test(lines[i])) {
                codeLines.push(lines[i])
                i++
            }
            i++ // skip closing ```
            blocks.push({type: 'code', content: codeLines.join('\n'), lang})
            continue
        }

        // Heading (requires space after #)
        const headingMatch = line.match(/^(#{1,4})\s+(.+)/)
        if (headingMatch) {
            blocks.push({type: 'heading', level: headingMatch[1].length, content: headingMatch[2]})
            i++
            continue
        }

        // Blockquote
        if (/^>\s*/.test(line)) {
            const quoteLines = []
            while (i < lines.length && /^>\s*/.test(lines[i])) {
                quoteLines.push(lines[i].replace(/^>\s*/, ''))
                i++
            }
            blocks.push({type: 'blockquote', lines: quoteLines})
            continue
        }

        // Unordered list
        if (/^[-*]\s+/.test(line)) {
            const items = []
            while (i < lines.length && /^[-*]\s+/.test(lines[i])) {
                items.push(lines[i].replace(/^[-*]\s+/, ''))
                i++
            }
            blocks.push({type: 'ul', items})
            continue
        }

        // Ordered list
        if (/^\d+\.\s+/.test(line)) {
            const items = []
            while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
                items.push(lines[i].replace(/^\d+\.\s+/, ''))
                i++
            }
            blocks.push({type: 'ol', items})
            continue
        }

        // Blank line — consume entire run; emit spacer only between content blocks
        // (not at leading/trailing positions) so user-typed blank lines render visibly
        if (line.trim() === '') {
            while (i < lines.length && lines[i].trim() === '') i++
            if (i < lines.length && blocks.length > 0) {
                blocks.push({type: 'blank'})
            }
            continue
        }

        // Paragraph: collect consecutive non-block lines
        const paraLines = []
        while (
            i < lines.length &&
            lines[i].trim() !== '' &&
            !/^```/.test(lines[i]) &&
            !/^#{1,6}\s/.test(lines[i]) &&
            !/^>\s*/.test(lines[i]) &&
            !/^[-*]\s+/.test(lines[i]) &&
            !/^\d+\.\s+/.test(lines[i])
            ) {
            paraLines.push(lines[i])
            i++
        }
        if (paraLines.length > 0) {
            blocks.push({type: 'paragraph', lines: paraLines})
        }
    }

    return blocks
}

const HEADING_SIZES = {1: 'text-2xl', 2: 'text-xl', 3: 'text-lg', 4: 'text-base'}

const renderBlock = (block, mentions, key) => {
    switch (block.type) {
        case 'heading': {
            const Tag = `h${block.level}`
            const size = HEADING_SIZES[block.level] || 'text-base'
            return (
                <Tag key={key} className={`${size} font-bold mt-2 mb-1`}>
                    {parseInline(block.content, mentions)}
                </Tag>
            )
        }

        case 'paragraph': {
            const content = []
            block.lines.forEach((line, li) => {
                parseInline(line, mentions).forEach((el, ei) => content.push(
                    typeof el === 'string' ? el : React.cloneElement(el, {key: `${li}-${ei}`})
                ))
                if (li < block.lines.length - 1) content.push(<br key={`br-${li}`}/>)
            })
            return <p key={key} className="m-0">{content}</p>
        }

        case 'code':
            return (
                <pre key={key} className="bg-gray-100 rounded p-2 overflow-x-auto my-1 text-sm font-mono">
                    {block.content}
                </pre>
            )

        case 'blockquote': {
            const inner = []
            block.lines.forEach((line, li) => {
                parseInline(line, mentions).forEach((el, ei) => inner.push(
                    typeof el === 'string' ? el : React.cloneElement(el, {key: `${li}-${ei}`})
                ))
                if (li < block.lines.length - 1) inner.push(<br key={`br-${li}`}/>)
            })
            return (
                <blockquote key={key} className="border-l-4 border-gray-300 pl-3 italic my-1">
                    {inner}
                </blockquote>
            )
        }

        case 'ul':
            return (
                <ul key={key} className="list-disc pl-4 my-1">
                    {block.items.map((item, li) => (
                        <li key={li}>{parseInline(item, mentions)}</li>
                    ))}
                </ul>
            )

        case 'ol':
            return (
                <ol key={key} className="list-decimal pl-4 my-1">
                    {block.items.map((item, li) => (
                        <li key={li}>{parseInline(item, mentions)}</li>
                    ))}
                </ol>
            )

        case 'blank':
            return <br key={key}/>

        default:
            return null
    }
}

export const mapKeywords = (children = '', keywords = [], setKeywords) => {
    if (!children || children === null) return null

    const escapeRegExp = (string) => {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    }

    const safeKeywords = Array.isArray(keywords) ? keywords : []

    const text = safeKeywords?.reduce((res, keyword) => {
        const arr = _.get(keyword, 'keyword_alt', '').split(',')
        const logRes = res
        for (var i = 0; i < arr.length; i++) {
            const regex = new RegExp(' ' + escapeRegExp(arr[i].trim() + ' '), 'i')
            res = res.replace(regex, function (match) {
                return ` @keyword:${keyword.idname}:${encodeURI(match)} `
            })
            if (res !== logRes) {
                if (setKeywords) {
                    _.pullAt(arr, i)
                    keywords = [
                        ...safeKeywords.filter((e) => e.idname !== keyword.idname),
                        {
                            ...keyword,
                            keyword_alt: arr.join(', '),
                        },
                    ]
                    setKeywords(keywords)
                }
                break
            }
        }

        return res
    }, children)

    return text
}

export const mapDocuments = (children = '', documents = [], setDocuments) => {
    if (!children || children === null) return null

    const escapeRegExp = (string) => {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    }

    const text = documents.reduce((res, document) => {
        const regex = new RegExp(
            ' ' + escapeRegExp(document.document_no.trim() + ' '),
            'i'
        )
        res = res.replace(regex, function (match) {
            return ` @docs:${document.idname}:${encodeURI(match)} `
        })

        return res
    }, children)

    return text
}

export const mapReferenceLinks = (
    children = '',
    referenceLinks = [],
    setReferenceLinks
) => {
    if (!children || children === null) return null

    const escapeRegExp = (string) => {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    }

    const text = referenceLinks.reduce((res, referenceLink) => {
        const regex = new RegExp(escapeRegExp(referenceLink.value), 'i')
        res = res.replace(regex, function (match) {
            return ` #url$${referenceLink.url}$${encodeURI(match)} `
        })

        return res
    }, children)

    return text
}

const Description = ({
                         className = 'text-base',
                         style = {},
                         mentions = [],
                         children = "",
                         placeholder = null,
                         compact = false,
                         truncatable = false,
                         maxLines = 3,
                         onExpand = () => {},
                         Wrapper = 'div'
                     }) => {
    const {handleClick} = useContext(FeedContext)
    const t = useTranslate()

    const contentRef = useRef(null)
    const [expanded, setExpanded] = useState(false)
    const [overflowing, setOverflowing] = useState(false)

    const normalizedChildren = useMemo(() => {
        if (!compact || !children) return children || ""
        return children.replace(/[\r\n]+/g, ' ').replace(/[ \t]{2,}/g, ' ').trim()
    }, [children, compact])

    const rendered = useMemo(() => {
        if (!normalizedChildren) return null
        if (compact) {
            return parseInline(normalizedChildren, mentions, t)
        }
        const blocks = parseBlocks(normalizedChildren)
        return blocks.map((block, i) => renderBlock(block, mentions, i, t))
    }, [normalizedChildren, mentions, compact, t])

    // Chỉ truncate phần text — ảnh/video/embed nằm ngoài Description nên luôn hiển thị đầy đủ
    useLayoutEffect(() => {
        if (!truncatable || expanded) return
        const el = contentRef.current
        if (!el) return
        setOverflowing(el.scrollHeight > el.clientHeight + 1)
    }, [truncatable, expanded, rendered])

    if (_.isEmpty(children)) {
        return placeholder
    }

    if (!truncatable) {
        return (
            <Wrapper
                style={style}
                className={classNames(
                    "overflow-hidden",
                    className
                )}>
                {rendered}
            </Wrapper>
        )
    }

    return (
        <Wrapper
            style={style}
            className={classNames(
                "overflow-hidden",
                className
            )}>
            <div
                ref={contentRef}
                style={!expanded ? {
                    display: '-webkit-box',
                    WebkitLineClamp: maxLines,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                } : undefined}>
                {rendered}
            </div>
            {overflowing && (
                <span
                    onClick={() => {
                        if (!expanded) onExpand()
                        setExpanded((v) => !v)
                    }}
                    className="inline-block mt-1 font-semibold text-color-400 hover:text-color-200 cursor-pointer">
                    {expanded ? t('see less') : t('see more')}
                </span>
            )}
        </Wrapper>
    )
}

export default Description
